import {Injectable} from '@angular/core';
import {ImportConfigApiService, ImportConfigListResponse} from 'src/services/import-configs/import-config.api.service';
import {ImportConfigPaginationService} from 'src/services/import-configs/import-config.pagination.service';
import {ListQueryParameter} from 'src/models/general/list.query.parameter.model';
import {combineLatest, forkJoin, merge, Observable, of} from 'rxjs';
import {distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {
	ImportConfigCreateModel,
	ImportConfigDetailModel,
	ImportConfigListModel
} from 'src/models/productImport/config/importConfig.model';
import {ImporterConfigMapping} from 'src/models/productImport/mapping/mapping.config.model';
import {UntypedFormGroup} from '@angular/forms';
import {PreviewDataFormEntry} from 'src/models/productImport/previewData.model';
import {ImportConfigFormService} from 'src/services/import-configs/import-config-form.service';
import {Pagination} from 'src/models/general/pagination.model';
import {SelectItem} from '../../models/general/select.item.model';
import {EmailProtocolTypes} from '../../models/productImport/enums/protocolTypes.enum';

@Injectable({
	providedIn: 'root'
})
export class ImportConfigFacade {

	constructor(private apiService: ImportConfigApiService,
	            private formService: ImportConfigFormService,
	            private paginationService: ImportConfigPaginationService) {
	}

	getImportConfigList(): Observable<ImportConfigListResponse | null> {
		return combineLatest([
			this.paginationService.getPaginationObservable(),
		]).pipe(
			distinctUntilChanged(),
			map(([pagination]: [Pagination]) => {
				// Only ListQueryParameter because we don't need a specialised params-object like OrderSearchParams
				// This needs to change if something other than sort & pagination is needed
				const params = new ListQueryParameter();
				params.initPagination(pagination);
				return params;
			}),
			switchMap((params: ListQueryParameter) => {
				return merge(
					of(null),
					this.apiService.getImportConfigList(params)
				);
			})
		);
	}

	getImportConfig(importConfigUuid: string): Observable<ImportConfigDetailModel> {
		return this.apiService.getImportConfig(importConfigUuid);
	}

	createNewImportConfig(formValues: ImportConfigCreateModel): Observable<ImportConfigDetailModel> {
		return this.apiService.createNewImportConfig(formValues);
	}

	updateImportConfig(importConfig: ImportConfigDetailModel): Observable<ImportConfigDetailModel> {
		return this.apiService.updateImportConfig(importConfig);
	}

	updateImportConfigMapping(updateModel: ImporterConfigMapping, importUuid: string | undefined): Observable<ImportConfigDetailModel> {
		return this.apiService.updateImportConfigMapping(updateModel, importUuid);
	}

	// Liefert das konfigurierte Mapping eines Importers (Quellfeld → Label-Feld,
	// inkl. Sub-Mappings). Der Exporter nutzt das, um seine Feldauswahl auf die
	// Felder einzuschränken, die der zugewiesene Importer tatsächlich befüllt.
	getImporterMapping(importUuid: string): Observable<ImporterConfigMapping> {
		return this.apiService.getMappingForConfig(importUuid);
	}

	deleteImportConfig(uuid: string): Observable<void> {
		return this.apiService.deleteImportConfig(uuid);
	}

	runImport(uuid: string): Observable<ImportConfigDetailModel> {
		return this.apiService.runImport(uuid);
	}

	updateCronStatus(uuid: string, shouldBeEnabled: boolean): Observable<void> {
		return this.apiService.updateCronStatus(uuid, shouldBeEnabled);
	}

	// ==================================================================================================================

	fillMappingFormWithMapping(uuid: string, mappingForm: UntypedFormGroup): Observable<UntypedFormGroup> {
		return forkJoin([
			this.apiService.getPreviewDataForConfig(uuid),
			this.apiService.getMappingForConfig(uuid)
		]).pipe(
			map(([previewDataResponse, importerMapping]: [PreviewDataFormEntry[], ImporterConfigMapping]) => {
				const previewData = this.formService.mapEmailDataToPreview(previewDataResponse);
				this.formService.fillMappingForm(mappingForm, previewData, importerMapping);
				return mappingForm;
			})
		);
	}

	// ==================================================================================================================


	handleProductIdentifierUpdate(checked: boolean, changedEntryIndex: number, form: UntypedFormGroup): void {
		return this.formService.handleProductIdentifierUpdate(checked, changedEntryIndex, form);
	}

	updatePagination(pagination: Pagination): void {
		this.paginationService.updatePagination(pagination);
	}

	getImporterAsSelectItem(): Observable<SelectItem[]> {

		const listParams = new ListQueryParameter();
		listParams.initPagination({currentPage: -1, pageSize: 999} as Pagination);

		return this.apiService.getImportConfigList(listParams).pipe(
			map((data: ImportConfigListResponse) => {
				return data.importer.map((importer: ImportConfigListModel) => {
					return {
						id: importer.uuid,
						value: importer.title
					} as SelectItem;
				});
			})
		);

	}

	hasEmailImporter(): Observable<boolean> {
		const listParams = new ListQueryParameter();
		listParams.initPagination({currentPage: -1, pageSize: 999} as Pagination);

		return this.apiService.getImportConfigList(listParams).pipe(
			map((data: ImportConfigListResponse) => {
				return data.importer.some((importer: ImportConfigListModel) => importer.hostProtocol === EmailProtocolTypes.MAIL);
			})
		);
	}
}
